/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */
/* eslint-disable max-statements */
/* eslint-disable no-extra-boolean-cast */

import AssertionError from 'assertion-error';
import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';

import ErrorName from '../../error/error-name';
import HtmlMediaUtil from '../../html/html-media-util';
import StretchNative from '../../stretch/stretch-native';
import ViewerLifeCycle from '../life-cycle/viewer-life-cycle';

/**
 * @import { IElement } from '../../video'
 * @import { EventTypes, ViewerController } from '../controller'
 * @import BrowserCapabilities from '../../capabilities/browser-capabilities'
 * @import ScheduleInfo from '../../schedule/schedule-info'
 */

// TODO Sdílí spoustu kódu s ostatními ovladači, DRY.
// - TizenSssp4VideoViewerController
// - WebosSignage3_0StreamViewerController
// TODO Sdílí CSS třídu viewer-controller-video s ostatními ovladači.
// - To je nevhodné z pohledu organizace, kde by mělo být jméno dle třídy.
// - Na druhou stranu aktuální přístup, kdy se třídy odebírají a přidávají
//   při každé změně média se také neosvědčil, protože na krátký okamžik nemusí
//   mít video element žádnou třídu, takže se zbytečně mění jeho rozměry.
// - Zatím tedy ponecháno takto, dokud nedojde k refaktorování na lepší řešení.
// TODO: #555: Opravit četné typové bugy.
/**
 * Ovladač pro zobrazování video souborů.
 * @implements {ViewerController}
 */
export default class ViewerControllerVideo {
  /**
   * Vytvoří instanci.
   * @param {BrowserCapabilities} browserCapabilities
   * @param {IElement} el
   * @param {Object} [opts={}]
   * @param {string|null} [opts.poster] Poster pro, který se zobrazí při zastaveném videu.
   * @param {boolean} [opts.preloadBlackeningFix] Příznak pro opravu černání videí.
   * @param {number} [opts.preloadBlackeningFixDelay] Hodnota určující zpoždění opravy černání videí
   * (doba zase kterou se nastaví původní hodnoty průhlednosti elementu).
   * @param {boolean} [opts.seekDisable] Příznak pro zakázání seekování videi u deterministického přehrávání.
   */
  constructor(
    browserCapabilities,
    el,
    {
      poster,
      preloadBlackeningFix,
      preloadBlackeningFixDelay,
      seekDisable
    } = {}
  ) {
    this.browserCapabilities = browserCapabilities;
    this.el = el;
    this.poster = poster;
    this.seekDisable = seekDisable;
    this.preloadBlackeningFix = preloadBlackeningFix;
    this.preloadBlackeningFixDelay = preloadBlackeningFixDelay;

    this.stretchNative = new StretchNative();
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter<EventTypes>}
     */
    this.emitter = new EventEmitter();
    /**
     * @private
     * @type {HtmlMediaUtil}
     */
    this.mediaUtil = new HtmlMediaUtil();
    /**
     * Callback pro selhání spouštění videa, pokud nějaké probíhá.
     * @private
     * @type {((error: Error) => void) | null}
     */
    this.playReject = null;
    this.playSeekReject = null;
    /**
     * Callback pro úspěch spouštění videa, pokud nějaké probíhá.
     * @private
     * @type {(() => void) | null}
     */
    this.playResolve = null;
    /**
     * Časovač pro maximální dobu čekání na spuštění přehrávání.
     * @private
     * @type {ReturnType<typeof setTimeout> | undefined}
     */
    this.playTimeout = undefined;
    this.positionLastTime = null;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    this.positionTimeout = undefined;
    /** @type {ScheduleInfo | null} */
    this.scheduleInfo = null;
    this.viewerLifeCycle = new ViewerLifeCycle();
    // Navázání naslouchání na instanci.
    this.onCanplaythrough = this.onCanplaythrough.bind(this);
    this.onEnded = this.onEnded.bind(this);
    this.onError = this.onError.bind(this);
    this.onPlaying = this.onPlaying.bind(this);
    this.error = null;
  }

  /**
   * Naváže naslouchání událostem z elementu.
   * @param {HTMLVideoElement} el Element, kterému se naslouchá.
   * @private
   * @returns {void}
   */
  bindElement(el) {
    el.addEventListener('canplaythrough', this.onCanplaythrough);
    el.addEventListener('ended', this.onEnded);
    el.addEventListener('error', this.onError);
    el.addEventListener('playing', this.onPlaying);
  }

  /** @type {ViewerController['destroy']} */
  destroy() {
    this.scheduleInfo?.releaseMaybeBlobUrl();
    this.stopPositionWatchdog();
    clearTimeout(this.playTimeout);
    this.unbindElement(this.el);
  }

  /** @type {ViewerController['enter']} */
  enter(offset) {
    const scheduleInfo = this.scheduleInfo;
    const { el } = this;

    return this.viewerLifeCycle
      .enter(() => {
        if (!scheduleInfo) {
          throw new AssertionError('schedule info not set');
        }
        // Určení všech potřebných hodnot.
        const volume = scheduleInfo.getVolume();

        // Kontrola jestli se již při preloadu nevyskytla chyba,
        // pokud se chyba vykytla, tak ji deleguji.
        if (this.error) {
          throw this.error;
        }

        // Zapnutí zvuku videa, pouze pokud je potřeba.
        // Zapnutí při volume na 0 může přerušit přehrávání zvuku jiného videa.
        if (volume > 0) {
          el.muted = false;
        }

        // Pokud je požadaváno seekování videa
        if (!isNil(offset) && offset > 0) {
          return this.playSeek(offset, el);
        }
        // Zahájení přehrávání videa.
        return this.play(el);
      })
      .catch((err) => {
        // Pro SeekError nechceme měnit název chyby.
        if (err.name === ErrorName.SeekError) {
          throw err;
        }
        err.name = ErrorName.EnterPlayingError;
        throw err;
      })
      .then(() => {
        // Visiblity a emitovani eventu je zadouci pouze,
        // pokud se podařilo video úspěšně spustit.

        // #505 - webOS 6.0 - nefunkční přehrávání videí
        el.style.visibility = 'visible';
        this.emitter.emit('playing');
      })
      .finally(() => {
        // Kontrola povolení FIXu černání.
        if (Boolean(this.preloadBlackeningFix)) {
          setTimeout(
            () => el.parentElement?.style.setProperty('opacity', '1.0'),
            this.preloadBlackeningFixDelay
          );
        }
      });
  }

  /** @type {ViewerController['leave']} */
  leave() {
    return this.viewerLifeCycle.leave(() => {
      // Video se zastaví.
      this.el.pause();
    });
  }

  /** @type {ViewerController['off']} */
  off(...args) {
    // @ts-ignore TODO #555: ViewerController
    this.emitter.off(...args);
  }

  /** @type {ViewerController['on']} */
  on(...args) {
    // @ts-ignore TODO #555: ViewerController
    this.emitter.on(...args);
  }

  /**
   * Reakce na dostatečné načtení zdroje v elementu.
   * @private
   * @returns {void}
   */
  onCanplaythrough() {
    this.emitter.emit('loaded');
    // Ošetření neočekávaného opakovaného vyvolaní události `canplaythrough`.
    // Dochází k němu v každé iteraci přehrávání při nastavení `loop`.
    // Pozorováno ale i jindy, např. při minimalizaci okna prohlížeče.
    this.el.removeEventListener('canplaythrough', this.onCanplaythrough);
  }

  /**
   * Dosažení konce přehrávání.
   * @private
   * @returns {void}
   */
  onEnded() {
    this.stopPositionWatchdog();
    this.emitter.emit('played');
  }

  /**
   * Reakce na chybu v elementu.
   *
   * Pokud probíhá pokus o spuštění videa,
   * tak se chybová událost interpretuje jako jeho selhání.
   * V opačném případě se chyba emituje.
   * @private
   * @returns {void}
   */
  onError() {
    this.stopPositionWatchdog();
    const error = this.mediaUtil.createError(this.el);
    const { playReject, playSeekReject } = this;
    this.error = error;
    if (!isNil(playSeekReject)) {
      return playSeekReject(error);
    }
    if (!isNil(playReject)) {
      return playReject(error);
    }
    this.emitter.emit('error', error);
  }

  /**
   * Reakce na skutečný začátek přehrávání.
   * Pokud probíhá pokus o spuštění videa,
   * tak se událost se začátkem přehráváním interpretuje jako jeho dokončení.
   * V opačném případě se nedělá nic.
   * @private
   * @returns {void}
   */
  onPlaying() {
    const { playResolve } = this;
    if (!isNil(playResolve)) {
      return playResolve();
    }
  }

  /**
   * Spustí přehrávání video elementu.
   *
   * Vzhledem ke zpětné kompatibilitě se starými Chrome se naslouchá události `playing`,
   * místo spolehání na `Promise` vrácenou metodou `play`.
   *
   * Je nutné naslouchat na událost `playing`, která informuje o skutečném začátku přehrávání.
   * Událost `play` informuje pouze o začátku pokusu o spuštění.
   * Exerimentálně bylo zjištěno, že stejně se chová i `Promise`,
   * která byla vrácena při volání `HTMLMediaElement.play`.
   *
   * Dokumentace:
   * [HTMLMediaElement.play returns a Promise](https://developers.google.com/web/updates/2016/03/play-returns-promise).
   * @param {HTMLVideoElement} el Element, který se má přehrát.
   * @private
   * @returns {Promise<void>}
   */
  play(el) {
    // Kontrola povolení FIXu černání.
    if (Boolean(this.preloadBlackeningFix)) {
      el.parentElement?.style.setProperty('opacity', '0.999');
    }

    return /** @type {Promise<void>} */ (
      new Promise((resolve, reject) => {
        this.playReject = reject;
        this.playResolve = resolve;
        const promise = el.play();
        if (!isNil(promise)) {
          promise.catch((error) => reject(error));
        }
        this.playTimeout = setTimeout(
          () => reject(new Error('Play request timed out.')),
          // TODO Jakou hodnotu zvolit? Přidat do konfigurace.
          30 * 1000
        );
        // Video currentPosition watchdog init
        this.positionLastTime = 0;
        this.startPositionWatchdog();
      })
    ).finally(() => {
      this.playReject = null;
      this.playResolve = null;
      clearTimeout(this.playTimeout);
    });
  }

  /**
   * Spustí video v daném čase.
   * Pokud je offset menší 1000 a seek není podporovaný, tak se video normálně spustí.
   * Pokud je offset větší než 1000 a seek není podporovaný, tak dojde k vyvolání výjimky.
   * Pokud je seek podporovaný, pak dojde k přetočení do daného času a následnému "doseeku" o zpozdění způsobené voláním play.
   * Podpora seeku se určuje na základě události seeked tj. pokud se po této události změní currentTime, pak je seek podporovaný.
   * @param {number} offset Posunutí začátku v ms .
   * @param {HTMLVideoElement} el Element, který se má přehrát.
   * @private
   * @returns {Promise<void>}
   */
  playSeek(offset, el) {
    let onSeeked;
    const delay = 1000;
    let seekSupported = true;

    const error = new Error('seek not supported, start is too delayed');
    error.name = ErrorName.SeekError;

    const offsetSeconds = offset / 1000;
    return new Promise((resolve, reject) => {
      this.playSeekReject = reject;
      onSeeked = () => {
        el.removeEventListener('seeked', onSeeked);
        const afterSeek = Date.now();
        const seekDelay = afterSeek - beforeSeek;

        // Na některých platformách dochází k ořezání desetinných míst po nastavení currentTime.
        // Bylo vypozorováno, že stačí mít toleranci 50 ms.
        if (Math.abs(el.currentTime - offsetSeconds) > 0.05) {
          seekSupported = false;
          if (offset + seekDelay > delay) {
            return reject(error);
          }
        }

        return play(seekDelay);
      };

      /**
       * @param {number} seekDelay
       * @returns {Promise<void>}
       */
      const play = async (seekDelay) => {
        try {
          const beforePlay = Date.now();
          await this.play(el);
          const afterPlay = Date.now();
          const playDelay = afterPlay - beforePlay;
          if (seekSupported) {
            const correctDelaySeeked = () => {
              el.removeEventListener('seeked', correctDelaySeeked);
              resolve(undefined);
            };
            el.addEventListener('seeked', correctDelaySeeked);
            el.currentTime = (offset + seekDelay + playDelay) / 1000;
            return undefined;
          } else if (offset + seekDelay + playDelay > delay) {
            // Pokud bylo video spuštěno, je nutné ho zastavit,
            // pokud bude vyhozena výjimka.
            this.el.pause();
            throw error;
          }
          return resolve(undefined);
        } catch (err) {
          return reject(err);
        }
      };

      const beforeSeek = Date.now();
      try {
        if (this.seekDisable || !this.browserCapabilities.seek()) {
          const seekError = new Error();
          seekError.name = ErrorName.SeekNotSupportedError;
          throw seekError;
        }
        el.addEventListener('seeked', onSeeked);
        el.currentTime = offsetSeconds;
      } catch {
        el.removeEventListener('seeked', onSeeked);
        // Přehrávání se nespouští, pokud je seek zakázaný/nepodporovaný
        // a offset je větší než přípustné zpoždění.
        if (offset > delay) {
          return reject(error);
        }
        // Nastavení currentTime může v prohlížečích vyhodit chybu, pokud je readyState < 1
        seekSupported = false;
        play(0);
      }
    });
  }

  /**
   * Spustí position watchdog
   * @private
   * @returns {void}
   */
  startPositionWatchdog() {
    // Experimentálně zjištěno na RPI3, že watchdog na 1000 ms škodí na pomalých zařízeních,
    // kde watchdog video zastaví ještě předtím, než je kompletně načteno a nemá šanci se vůbec spustit.
    this.positionTimeout = setTimeout(this.positionWatchdog.bind(this), 5000);
  }

  /**
   * Zastaví position watchdog
   * @private
   * @returns {void}
   */
  stopPositionWatchdog() {
    clearTimeout(this.positionTimeout);
  }

  /**
   * Ochrana proti zaseknutí přehrávání videa kontrolou currentTime.
   * Slouží eliminaci chyb v Chromiu, kdy se video zastaví (event waiting), ale
   * již poté nepokračuje dále v přehrávání.
   * Pozorováno na Chromiu 90 / Gilbarco, nelze spolehlivě simulovat.
   * @private
   * @returns {undefined}
   */
  positionWatchdog() {
    const { el } = this;
    if (el.currentTime === this.positionLastTime) {
      // https://developer.mozilla.org/en-US/docs/Web/API/MediaError
      const error = new Error(`Position timeout: ${el.src}`);
      // Zvoleno nejbližší možné MEDIA_ERR_DECODE
      // https://developer.mozilla.org/en-US/docs/Web/API/MediaError/code#media_error_code_constants
      // @ts-expect-error TODO #555: MediaError
      error.code = 3;
      if (isNil(this.playReject)) {
        // @ts-expect-error TODO #555: MediaError
        this.emitter.emit('error', this.mediaUtil.createError({ error }));
      } else {
        this.playReject(error);
      }
      return;
    }
    this.positionLastTime = el.currentTime;
    this.startPositionWatchdog();
  }

  /** @type {ViewerController['release']} */
  release() {
    return this.viewerLifeCycle.release(() => {
      this.scheduleInfo?.releaseMaybeBlobUrl();
      // Uvolnění zdrojového elementu.
      this.unbindElement(this.el);
      // Zrušení hodnot v instanci.
      this.scheduleInfo = null;
    });
  }

  /** @type {ViewerController['seize']} */
  seize(scheduleInfo) {
    return this.viewerLifeCycle.seize(() => {
      // Nastavení hodnot v instanci.
      this.scheduleInfo = scheduleInfo;
      // Určení všech potřebných hodnot.
      return scheduleInfo.getUrl().then((url) => {
        const stretchEnum = scheduleInfo.getStretchEnum();
        const stretchClassName = this.stretchNative.getClassName(stretchEnum);
        const volume = scheduleInfo.getVolume();
        const { el } = this;
        // Zahájí se naslouchání událostem z video elementu.
        this.bindElement(el);
        // Nastaví se CSS třídy.
        el.classList.add('viewer-controller-video', stretchClassName);
        el.style.visibility = 'hidden';
        // @ts-expect-error Speciální property pro WebOS pro pro zlepšení výkonu.
        el.texture = true;
        // Na některých platformách, kde je prohližeč >= Chromium 121 se objevovala Chromecast ikona.
        el.disableRemotePlayback = true;
        // Zablokuje se zpracování zvuku preloadovaného videa.
        // Některé platformy podporují přehrávání pouze jednoho zdroje zvuku
        // a bez muted by mohlo zrušit zvuk u jiného videa.
        el.muted = true;
        if (!isNil(this.poster)) {
          // Některé webové platformy (Mersive) nedodržují standard a mají vlastní poster obrázek, vynutí se prázdný.
          // Nastavení tohoto atributu ale rozhodí přehrávání videa u webOS 4.0,
          // proto je vyčleněno do samostatné implementace.
          el.poster = this.poster;
        }
        // Nastaví se hlasitost, ve které se video později přehraje.
        el.volume = volume;
        // Nastaví se zdroj, což zahájí načítání videa.
        el.src = url;
      });
    });
  }

  /**
   * Uvolní naslouchání událostem z elementu.
   * @param {HTMLVideoElement} el Element, kterému se naslouchá.
   * @private
   * @returns {void}
   */
  unbindElement(el) {
    el.removeEventListener('canplaythrough', this.onCanplaythrough);
    el.removeEventListener('ended', this.onEnded);
    el.removeEventListener('error', this.onError);
    el.removeEventListener('playing', this.onPlaying);
  }
}
